import { createApp } from 'vue'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createHead } from '@unhead/vue/client'
import * as Sentry from '@sentry/vue'
import router from '@/router'
import App from './App.vue'
import './style.css'
import 'flowbite'
import { initAnalytics, useAnalytics } from '@/composables/useAnalytics'
import { initFeatureFlags, createPostHogFeatureFlagProvider } from '@/composables/useFeatureFlags'
import { createPostHogProvider } from '@/analytics/posthog'
import { captureUtmParams } from '@/utils/utm'

// Font Awesome icons — curated registry (side-effect import registers the icons)
import './fontawesome'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

const app = createApp(App)

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.2,
    ignoreErrors: [
      // Google Identity Services SDK noise — fires when GIS tries to update
      // a button container during navigation. Harmless, login still works.
      /Object Not Found Matching Id/,
    ],
  })
}

const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)
app.use(pinia)
app.use(createHead())
app.use(router)
app.component('FontAwesomeIcon', FontAwesomeIcon)

// Capture UTM / ref params from landing URL before router strips them
captureUtmParams()

// Analytics — only initializes if VITE_POSTHOG_KEY is set
const posthogKey = import.meta.env.VITE_POSTHOG_KEY as string | undefined
if (posthogKey) {
  initAnalytics(createPostHogProvider(posthogKey, 'https://eu.i.posthog.com'))
  initFeatureFlags(createPostHogFeatureFlagProvider())
}
const analytics = useAnalytics()
router.afterEach(() => analytics.capturePageView())

app.mount('#app')
